import { useState } from "react";
import { ChildrenType, PlacesType } from "react-tooltip";

import { Tooltip } from "./Tooltip";

/**
 * The props that must be implemented on the content rendered for an automatic tooltip.
 */
interface IContentWithOverflowTooltipContentProps {
    /**
     * The HTML ID for the returned element. This must match {@link IContentWithOverflowTooltipProps.id}.
     */
    id: string;

    onMouseEnter: (mouseEvent: React.MouseEvent<HTMLElement>) => void;
    onMouseLeave: () => void;
}

/**
 * The properties for an {@link AutoTooltip} component.
 */
interface IContentWithOverflowTooltipProps {
    /**
    * Render the content that has an auto tooltip attached. The rendered element must apply the given props for the tooltip to work correctly.
    */
    renderContent: (childProps?: IContentWithOverflowTooltipContentProps) => ChildrenType;

    /**
    * Where the component should render relative to the rendered content.
    */
    place: PlacesType;

    /**
    * The HTML ID of the rendered content to use as an anchor.
    */
    id: string;
}

/**
 * A component which renders content, with an automatic tooltip on hover if it can't be fully shown horizontally.
 */
function ContentWithOverflowTooltip(props: Readonly<IContentWithOverflowTooltipProps>): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);

    function onMouseEnter(mouseEvent: React.MouseEvent<HTMLElement>): void {
        const element = mouseEvent.target as HTMLElement;

        setIsOpen(element.scrollWidth >= element.clientWidth);
    }

    return (
        <>
            {props.renderContent({ id: props.id, onMouseEnter, onMouseLeave: () => setIsOpen(false) })}
            {isOpen && (
                <Tooltip
                    anchorSelect={`#${props.id}`}
                    isOpen
                    place={props.place}
                >
                    {props.renderContent()}
                </Tooltip>
            )}
        </>
    )
    ;
}

export { ContentWithOverflowTooltip, IContentWithOverflowTooltipContentProps, IContentWithOverflowTooltipProps };
